import { GENERATION_TIMEOUT_MS } from '@/features/image-gif-maker/model/gifMaker'
import type {
  GifWorkerFailureCode,
  GifWorkerRequest,
  GifWorkerResponse,
} from '@/features/image-gif-maker/model/types'

interface WorkerLike {
  onmessage: ((event: MessageEvent<GifWorkerResponse>) => void) | null
  onerror: ((event: ErrorEvent) => unknown) | null
  postMessage: (request: GifWorkerRequest) => void
  terminate: () => void
}

export type GifGenerationFailure = GifWorkerFailureCode | 'worker_failed' | 'timeout' | 'cancelled'

export interface GifGenerationOwner {
  cancel: () => void
}

interface Callbacks {
  progress: (completed: number, total: number) => void
  success: (blob: Blob, durationMs: number) => void
  error: (code: GifGenerationFailure) => void
}

interface Dependencies {
  createWorker?: () => WorkerLike
  setTimer?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void
  now?: () => number
}

export function startGifGeneration(
  request: GifWorkerRequest,
  callbacks: Callbacks,
  dependencies: Dependencies = {},
): GifGenerationOwner {
  const setTimer = dependencies.setTimer ?? setTimeout
  const clearTimer = dependencies.clearTimer ?? clearTimeout
  const now = dependencies.now ?? performance.now.bind(performance)
  const startedAt = now()
  let worker: WorkerLike | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let settled = false

  const cleanup = () => {
    if (timer !== null) {
      clearTimer(timer)
      timer = null
    }
    if (worker) {
      worker.onmessage = null
      worker.onerror = null
      worker.terminate()
      worker = null
    }
  }
  const fail = (code: GifGenerationFailure) => {
    if (settled) return
    settled = true
    cleanup()
    callbacks.error(code)
  }
  const succeed = (bytes: Uint8Array) => {
    if (settled) return
    settled = true
    cleanup()
    callbacks.success(new Blob([bytes], { type: 'image/gif' }), Math.max(0, now() - startedAt))
  }

  try {
    const created = dependencies.createWorker?.() ?? new Worker(
      new URL('../worker/gifMaker.worker.ts', import.meta.url),
      { type: 'module' },
    )
    worker = created
    created.onmessage = (event: MessageEvent<GifWorkerResponse>) => {
      const response = event.data
      if (response.kind === 'progress') {
        if (!settled) callbacks.progress(response.completed, response.total)
      } else if (response.kind === 'success') {
        succeed(response.bytes)
      } else {
        fail(response.code)
      }
    }
    created.onerror = (_event: ErrorEvent) => fail('worker_failed')
    timer = setTimer(() => fail('timeout'), GENERATION_TIMEOUT_MS)
    created.postMessage(request)
  } catch {
    fail('worker_failed')
  }

  return { cancel: () => fail('cancelled') }
}
